/**
 * The heart on a word page.
 *
 * Styled from the mockup's `.b-save` (docs/design/word-page-redesign.html):
 * coral outline on white, filling in once the word is saved.
 *
 * Hydration contract, which is why there is no spinner and no suspense: the
 * server render and the first client render are identical, because the store is
 * 'idle' and auth is 'loading' in both. The button therefore starts unsaved
 * everywhere, and flips only once /api/me has answered. An outline heart that
 * briefly fills is a better experience than a placeholder, and it is the only
 * shape that cannot mismatch.
 *
 * It never makes a request of its own to find out its state: the whole favorite
 * set is already in the store from one call per session.
 */

import React from 'react'
import { useDispatch } from 'react-redux'
import { useAuth } from 'lib/auth/auth-context'
import { isAuthConfigured } from 'lib/firebase'
import { requestSignIn, SaveWordSignInMessage } from 'api/auth-gate'
import { useFavoritesStore } from 'hooks/store-hooks'
import { favoriteKey } from 'store/favorites.reducer'
import { toggleFavorite } from 'services/favorites.service'
import type { SavedLang } from '../../../shared/favorites-contract'

type Props = {
   lang: SavedLang
   /**
    * The canonical headword from loader data — `dictionaryWord.word`, never
    * `params.word`. /english/Hello resolves to a different entry than it spells,
    * and saving the URL's spelling creates a favorite that is dangling from the
    * moment it exists.
    */
   word: string
}

const SaveWordButton = ({ lang, word }: Props) => {
   const dispatch = useDispatch()
   const { state } = useAuth()
   const favorites = useFavoritesStore()

   const key = favoriteKey(lang, word)
   const saved = favorites.byKey[key]
   const isPending = favorites.pending[key] === true

   const handleClick = async () => {
      // A double-click is a real thing on a toggle; the second one is dropped
      // rather than racing the first.
      if (isPending) return

      if (state.status !== 'signed-in') {
         const signedIn = await requestSignIn(SaveWordSignInMessage, {
            // The page behind the dialog is fine — they were reading a word —
            // so dismissing must not throw them to the homepage the way an API
            // refusal does.
            strandsPage: false,
            source: 'save_word',
         })
         if (!signedIn) return
         // useFavoritesSync loads the set on the uid change; the save below
         // does not wait for it, since the server is the authority either way.
      }

      await toggleFavorite(dispatch, lang, word, saved ?? null)
   }

   // Mirrors UserAvatar: with no auth configured the button could only ever
   // fail, so there is nothing honest to render.
   if (!isAuthConfigured) return null

   return (
      <button
         type="button"
         // Saved changes the heart, not the button: the chrome stays the white
         // pill it always was and only the glyph goes coral, so the control sits
         // next to the voice pill as a peer rather than turning into a solid
         // block that outweighs the headword beside it.
         className={`flex items-center justify-center h-[44px] px-[14px] shrink-0 rounded-full border border-hairline bg-transparent cursor-pointer transition-all duration-150 text-[14px] font-medium gap-[6px] hover:bg-usageBg hover:border-textMuted [&_.material-icons]:text-[18px] ${
            saved ? 'text-coral' : 'text-bodyBlack'
         } ${isPending ? 'opacity-70' : ''}`}
         title={saved ? 'Saved — click to remove' : 'Save word'}
         aria-label={saved ? 'Remove from saved words' : 'Save word'}
         aria-pressed={saved !== undefined}
         onClick={() => void handleClick()}
      >
         <span className="material-icons">{saved ? 'favorite' : 'favorite_border'}</span>
      </button>
   )
}

export default SaveWordButton
